import React from "react";
import { LegalPageLayout, LegalSection } from "../../components/LegalPageLayout";

const sections = [
  { id: "what-is-cookie", label: "What is a cookie?" },
  { id: "categories", label: "Cookie categories" },
  { id: "our-cookies", label: "Cookies we use" },
  { id: "third-party", label: "Third-party cookies" },
  { id: "your-choices", label: "Your choices" },
  { id: "dnt", label: "Do-Not-Track" },
  { id: "contact", label: "Contact" },
];

export default function CookiePolicy() {
  return (
    <LegalPageLayout
      eyebrow="Legal · 03"
      title="Cookie Policy"
      updated="February 20, 2026"
      intro="This Cookie Policy explains what cookies and similar technologies EVIQ uses, why we use them, and how you can control them. For everything about how we handle personal information, see the full Privacy Policy."
      sections={sections}
    >
      <LegalSection id="what-is-cookie" title="1. What is a cookie?">
        <p>
          A cookie is a small text file that a website places on your browser to remember
          preferences, keep you signed in, or measure how the site is used. Similar
          technologies (localStorage, sessionStorage, pixels) work in comparable ways.
        </p>
      </LegalSection>

      <LegalSection id="categories" title="2. Cookie categories">
        <ul>
          <li>
            <strong>Strictly necessary.</strong> Required for the site to function
            (authentication, security, load-balancing). These cannot be disabled.
          </li>
          <li>
            <strong>Preferences.</strong> Remember your language, selected vehicle, and
            other choices so you don&rsquo;t have to set them every visit.
          </li>
          <li>
            <strong>Analytics.</strong> Aggregated, anonymized usage data used to
            understand which parts of EVIQ are useful and which are broken.
          </li>
          <li>
            <strong>Marketing.</strong> Used to measure conversion from ads and to show
            you relevant content on third-party sites. <em>EVIQ currently does not run
            any marketing cookies.</em>
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="our-cookies" title="3. Cookies we use">
        <div className="overflow-x-auto -mx-4 sm:mx-0 border border-white/10 rounded-xl bg-white/[0.02]">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-white/10 text-left text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Purpose</th>
                <th className="px-4 py-3">Duration</th>
              </tr>
            </thead>
            <tbody className="text-zinc-300">
              <Row name="eviq_session" cat="Strictly necessary" purpose="Keeps you signed in during a browsing session." dur="Session" />
              <Row name="eviq.subscription_status" cat="Strictly necessary" purpose="Remembers whether you have an active subscription so features unlock correctly." dur="Persistent" />
              <Row name="eviq.subscription_tier" cat="Strictly necessary" purpose="Tracks whether Premium features should be unlocked." dur="Persistent" />
              <Row name="i18nextLng" cat="Preferences" purpose="Remembers your language selection (EN / ES)." dur="Persistent" />
              <Row name="tesla_session_id" cat="Strictly necessary" purpose="Links your browser to your authorized Tesla OAuth session." dur="Persistent" />
            </tbody>
          </table>
        </div>
      </LegalSection>

      <LegalSection id="third-party" title="4. Third-party cookies">
        <p>The following third-party services may set cookies on EVIQ:</p>
        <ul>
          <li><strong>Stripe</strong> — sets cookies on our checkout to prevent card fraud and PCI-compliance requirements. Stripe&rsquo;s <a href="https://stripe.com/cookie-settings" target="_blank" rel="noreferrer">cookie policy</a>.</li>
          <li><strong>PostHog</strong> (product analytics) — sets a first-party cookie in your browser to distinguish visitors and measure feature usage. IPs are not stored.</li>
        </ul>
        <p>
          We do <strong>not</strong> currently use Google Analytics, Facebook Pixel,
          Microsoft Clarity, or ad-retargeting cookies. If that ever changes, this page
          will be updated <em>before</em> the change goes live.
        </p>
      </LegalSection>

      <LegalSection id="your-choices" title="5. Your choices">
        <p>You can control cookies in several ways:</p>
        <ul>
          <li>Configure your browser to block or delete cookies (see your browser&rsquo;s help pages).</li>
          <li>Use private/incognito mode.</li>
          <li>Email <a href="mailto:support@eviq.pro">support@eviq.pro</a> to request an export or deletion of any analytics data associated with your account.</li>
        </ul>
        <p>
          Note: blocking &ldquo;strictly necessary&rdquo; cookies will break the ability
          to sign in and hold a subscription session.
        </p>
      </LegalSection>

      <LegalSection id="dnt" title="6. Do-Not-Track">
        <p>
          EVIQ honors the browser <code>DNT: 1</code> header where technically possible —
          when it is set, our analytics service is instructed to record only anonymous
          aggregate events and not to persist a visitor cookie.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="7. Contact">
        <p>
          Questions? Write to{" "}
          <a href="mailto:support@eviq.pro">support@eviq.pro</a>.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}

const Row = ({ name, cat, purpose, dur }) => (
  <tr className="border-b border-white/5 last:border-0">
    <td className="px-4 py-3 font-mono text-[12px] text-white whitespace-nowrap">{name}</td>
    <td className="px-4 py-3 text-[13px] text-zinc-400">{cat}</td>
    <td className="px-4 py-3 text-[13px]">{purpose}</td>
    <td className="px-4 py-3 text-[13px] text-zinc-400 whitespace-nowrap">{dur}</td>
  </tr>
);
